import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { all_routes } from "../../../router/all_routes";
import CollapseHeader from "../../../../core/common/collapse-header/collapse-header";
import Footer from "../../../../core/common/Footer";
import useApi from "../../../../core/customhook/useApi";
import webservice_url from "../../../../core/webservice/Webservice_url";
import Swal from "sweetalert2";
import z from "zod";
import SnipperLoader from "../../../../core/common/loader/SnipperLoader";

const packageSchema = z.object({
  packageName: z.string().trim().min(1, "Package Name is required."),
  geoId: z.string().trim().min(1, "GEO is required."),
  packageCode: z.string().trim().min(1, "Package Code is required."),
});
 
const EMPLOYMENT_CHECK_IDS = [
  "b225216b-ec67-4b70-a4f0-61ce4460b8bb", // Employment
  "1f12f689-b17a-409b-b433-bc7d685ca84f", // Employment - 2 Year
  "2c825e1d-10d2-4caf-b12d-b1df9c21b806", // Employment - 3 Year
  "1eda7972-29b4-40d4-a688-44a3afc2c360", // Employment - 5 Year
  "d904e066-1c35-4525-b971-7aaaa8cbde44", // Employment - 7 Year
  "a0057787-887b-4d45-9da3-5a758e4b6964", // Employment - 10 Year
];

const EDUCATION_CHECK_IDS = [
  "550f294a-257d-4b87-a0e7-8b34a3223d41", // 2 Highest Education
  "66b2e258-615e-4998-b51b-b8b7ec5a2417", // All Education Documents
  "1d479262-b776-4bb5-8e52-3a6919556f5d", // Education Documents
  "a687f91f-1c4c-4761-a4fe-37a564887783", // Highest Education
];
 
const isDrugTest = (check: any) => /^drug test/i.test(check?.check_name ?? "");
const isNonAssisted = (check: any) => check?.check_type === "NON-ASSISTED";
const CreatePackage = () => {
  const navigate = useNavigate();
  const [countries, setCountries] = useState<any[]>([]);
  const [checks, setChecks] = useState<any[]>([]); 
  const [formData, setFormData] = useState({
    packageName: "",
    geoId: "",
    packageCode: "",
  });
  const [errors, setErrors] = useState({
    packageName: "",
    geoId: "",
    packageCode: "",
  });

  const [selectedChecks, setSelectedChecks] = useState<string[]>([]);
  const defaultsApplied = useRef(false);
  const getCountries = useApi<any>(webservice_url.countries, {
    method: "GET",
    autoCall: false,
    handleGlobalError: false,
  });
  const createPackage = useApi<any>("/api/v1/admin/packages/checks", {
    method: "POST",
    autoCall: false,
    handleGlobalError: false,
  });
  const getCheckHeader = useApi<any>("/api/v1/admin/check-headers", {
    method: "GET",
    autoCall: false,
    handleGlobalError: false,
  });
  const checkHeaderLoading = getCheckHeader.loading;
  const createLoading = createPackage.loading;

  useEffect(() => {
    const fetchDropdowns = async () => {
      try {
        const countriesRes = await getCountries.callApi();
        if (countriesRes?.success) {
          setCountries(countriesRes.data || []);
        }
      } catch (error) {
        console.error("Dropdown API Error:", error);
      }
    };

    const fetchCheckHeader = async () => {
      try {
        const res = await getCheckHeader.callApi();
        if (res?.success) {
          setChecks(res.data || []);
        }
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Unable to load checks.",
        });
      }
    };
    fetchDropdowns();
    fetchCheckHeader();
  }, []);

  const allChecks = useMemo(
    () => checks.flatMap((header: any) => header.checks ?? []),
    [checks],
  );

  const nonAssistedChecks = useMemo(
    () => allChecks.filter(isNonAssisted),
    [allChecks],
  );

  const assistedChecks = useMemo(
    () => allChecks.filter((c: any) => !isNonAssisted(c)),
    [allChecks],
  );

  const employmentChecks = useMemo(
    () =>
      EMPLOYMENT_CHECK_IDS.map((id) =>
        assistedChecks.find((c: any) => c.check_id === id),
      ).filter(Boolean),
    [assistedChecks],
  );

  const educationChecks = useMemo(
    () =>
      EDUCATION_CHECK_IDS.map((id) =>
        assistedChecks.find((c: any) => c.check_id === id),
      ).filter(Boolean),
    [assistedChecks],
  );

  const otherAssistedChecks = useMemo(
    () =>
      assistedChecks.filter(
        (c: any) =>
          !EMPLOYMENT_CHECK_IDS.includes(c.check_id) &&
          !EDUCATION_CHECK_IDS.includes(c.check_id),
      ),
    [assistedChecks],
  );

  const drugTestIds = useMemo(
    () => allChecks.filter(isDrugTest).map((c: any) => c.check_id),
    [allChecks],
  );
 
  const sop = useMemo(
    () =>
      allChecks
        .filter((c: any) => selectedChecks.includes(c.check_id))
        .map((c: any) => c.check_name)
        .join(" + "),
    [allChecks, selectedChecks],
  );

  useEffect(() => {
    if (defaultsApplied.current || !allChecks.length) return;
    defaultsApplied.current = true;
    const defaults = allChecks
      .filter((c: any) => isNonAssisted(c) && !isDrugTest(c))
      .map((c: any) => c.check_id);
    setSelectedChecks((prev) => Array.from(new Set([...prev, ...defaults])));
  }, [allChecks]);

  const handleCheckToggle = (checkId: string) => {
    setSelectedChecks((prev) => {
      if (prev.includes(checkId)) {
        return prev.filter((id) => id !== checkId);
      }

      if (drugTestIds.includes(checkId)) {
        return [...prev.filter((id) => !drugTestIds.includes(id)), checkId];
      }

      if (EMPLOYMENT_CHECK_IDS.includes(checkId)) {
        return [
          ...prev.filter((id) => !EMPLOYMENT_CHECK_IDS.includes(id)),
          checkId,
        ];
      }
      if (EDUCATION_CHECK_IDS.includes(checkId)) {
        return [
          ...prev.filter((id) => !EDUCATION_CHECK_IDS.includes(id)),
          checkId,
        ];
      }
      return [...prev, checkId];
    });
  };

  const clearError = (field: string) => {
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleCreatePackage = async () => {
    const result = packageSchema.safeParse(formData);
    if (!result.success) {
      const fieldErrors = result.error.flatten().fieldErrors;
      setErrors({
        packageName: fieldErrors.packageName?.[0] || "",
        geoId: fieldErrors.geoId?.[0] || "",
        packageCode: fieldErrors.packageCode?.[0] || "",
      });
      return;
    }
    if (selectedChecks.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "Validation",
        text: "Please select at least one check.",
      });
      return;
    }
    const payload = {
      packageName: formData.packageName,
      geoId: formData.geoId,
      packageCode: formData.packageCode,
      sop,
      checks: selectedChecks.map((id) => ({ checkId: id })),
    };

    try {
      const res = await createPackage.callApi(payload);
      if (res?.success) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Package created successfully.",
        });
        navigate(all_routes.allPackage);
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: res?.error || res?.message || "Something went wrong.",
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Unable to create package.",
      });
    }
  };

  const renderCheckCard = (check: any) => {
    const selected = selectedChecks.includes(check.check_id);
    return (
      <div key={check.check_id} className="col-xl-3 col-lg-4 col-md-6 mb-3">
        <div
          onClick={() => handleCheckToggle(check.check_id)}
          className="rounded-3 px-3 py-3"
          style={{
            cursor: "pointer",
            transition: "all .2s ease",
            backgroundColor: selected ? "#E1F5EE" : "#fff",
            border: selected ? "1px solid #0F6E56" : "1px solid #E5E3DC",
          }}
        >
          <div className="d-flex align-items-center justify-content-between">
            <h6
              className="mb-0 fw-semibold"
              style={{
                color: selected ? "#085041" : "#2C2C2A",
                fontSize: "15px",
              }}
            >
              {check.check_name}
            </h6>

            <input
              type="checkbox"
              className="form-check-input"
              checked={selected}
              onClick={(e) => e.stopPropagation()}
              onChange={() => handleCheckToggle(check.check_id)}
              style={{
                width: 18,
                height: 18,
                cursor: "pointer",
                accentColor: "#0F6E56",
              }}
            />
          </div>
        </div>
      </div>
    );
  };
 
  const renderChooseOne = (label: string, list: any[]) => {
    if (!list.length) return null;
    return (
      <>
        <h6 className="fw-semibold mb-3" style={{ color: "#085041" }}>
          {label}
        </h6>
        <div className="row">{list.map(renderCheckCard)}</div>
        <hr className="mt-1 mb-3" />
      </>
    );
  };

  const renderGroup = (
    title: string,
    subtitle: string,
    list: any[],
    topSections?: React.ReactNode,
    gridList: any[] = list,
  ) => {
    if (!list.length) return null; 
    const selectedCount = list.filter((c: any) =>
      selectedChecks.includes(c.check_id),
    ).length;

    return (
      <div className="mb-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h5 className="fw-bold mb-1">{title}</h5>
            <small className="text-muted">{subtitle}</small>
          </div>
          <span className="badge"
            style={{ background: selectedCount ? "#0F6E56" : "#E1F5EE", color: selectedCount ? "#fff" : "#085041"}}>
            {selectedCount} Selected
          </span>
        </div>
        {topSections}
        <div className="row">{gridList.map(renderCheckCard)}</div>
      </div>
    );
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
          <div className="my-auto mb-2">
            <h2 className="mb-1">Create Package</h2>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={all_routes.superAdminDashboard}>
                    <i className="ti ti-smart-home" />
                  </Link>
                </li>
                <li className="breadcrumb-item">Account Activation</li>
                <li className="breadcrumb-item">Account Configuration</li>
                <li className="breadcrumb-item">Package</li>
                <li className="breadcrumb-item active">Create Package</li>
              </ol>
            </nav>
          </div>
          <div className="head-icons ms-2">
            <CollapseHeader />
          </div>
        </div>

        <div className="card mt-3 shadow-sm border-0">
          <div className="card-body">
            <div>
              <h4 className="fw-bold mb-4">Create Package</h4>
              <hr className="bg-dark mt-0" />
                <div className="row g-3 mb-2">
                <div className="col-md-4">
                  <label className="form-label ">
                    Package Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-control ${errors.packageName ? "is-invalid" : ""}`}
                    value={formData.packageName}
                    onChange={(e) => {
                      setFormData({ ...formData, packageName: e.target.value });
                      clearError("packageName");
                    }}
                  />
                  {errors.packageName && (
                    <div className="invalid-feedback">{errors.packageName}</div>
                  )}
                </div>

                <div className="col-md-4">
                  <label className="form-label ">
                    GEO <span className="text-danger">*</span>
                  </label>
                  <select
                    className={`form-select ${errors.geoId ? "is-invalid" : ""}`}
                    value={formData.geoId}
                    onChange={(e) => {
                      setFormData({ ...formData, geoId: e.target.value });
                      clearError("geoId");
                    }}
                  >
                    <option value="">Select GEO</option>
                    {countries.map((item: any) => (
                      <option key={item.country_id} value={item.country_id}>
                        {item.country_name}
                      </option>
                    ))}
                  </select>
                  {errors.geoId && (
                    <div className="invalid-feedback">{errors.geoId}</div>
                  )}
                </div>

                <div className="col-md-4">
                  <label className="form-label ">
                    Package Code <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-control ${errors.packageCode ? "is-invalid" : ""}`}
                    value={formData.packageCode}
                    onChange={(e) => {
                      setFormData({ ...formData, packageCode: e.target.value });
                      clearError("packageCode");
                    }}
                  />
                  {errors.packageCode && (
                    <div className="invalid-feedback">{errors.packageCode}</div>
                  )}
                </div>
              </div>

              <div className="mb-4">
                <label className="form-label">Scope of Package</label>
                <input
                  type="text"
                  className="form-control"
                  value={sop}
                  disabled
                />
              </div>
            </div>
            <hr className="bg-dark mt-0" />
            {checkHeaderLoading ? (
              <SnipperLoader />
            ) : (
              <div>
                {renderGroup(
                  "Non-Assisted Checks",
                  "Included by default. For Drug Test, choose only one panel.",
                  nonAssistedChecks,
                )}
                {renderGroup(
                  "Assisted Checks",
                  "Select the assisted checks to include in this package.",
                  assistedChecks,
                  <>
                    {renderChooseOne(
                      "Employment – choose only one",
                      employmentChecks,
                    )}
                    {renderChooseOne(
                      "Education – choose only one",
                      educationChecks,
                    )}
                  </>,
                  otherAssistedChecks,
                )}
              </div>
            )}

            <div className="d-flex justify-content-end gap-2 align-items-center mt-3">
              <button
                type="button"
                className="btn btn-outline-primary d-flex align-items-center gap-2"
                onClick={() => navigate(-1)}
              >
                <span>Back</span>
              </button>
              <button type="button" className="btn btn-primary d-flex align-items-center gap-2" onClick={handleCreatePackage} disabled={createLoading}>
                <span>{createLoading ? "Creating..." : "Create"}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default CreatePackage;

// import React, { useEffect, useMemo, useRef, useState } from "react";
// import { Link, useNavigate } from "react-router-dom";
// import { all_routes } from "../../../router/all_routes";
// import CollapseHeader from "../../../../core/common/collapse-header/collapse-header";
// import Footer from "../../../../core/common/Footer";
// import useApi from "../../../../core/customhook/useApi";
// import webservice_url from "../../../../core/webservice/Webservice_url";
// import Swal from "sweetalert2";
// import z from "zod";
// import SnipperLoader from "../../../../core/common/loader/SnipperLoader";

// const packageSchema = z.object({
//   packageName: z.string().trim().min(1, "Package Name is required."),
//   geoId: z.string().trim().min(1, "GEO is required."),
//   packageCode: z.string().trim().min(1, "Package Code is required."),
// });

// /* ---------- Check rules ---------- */

// // Only one of these can be selected at a time
// const EMPLOYMENT_CHECK_IDS = [
//   "b225216b-ec67-4b70-a4f0-61ce4460b8bb",
//   "2c825e1d-10d2-4caf-b12d-b1df9c21b806",
//   "1eda7972-29b4-40d4-a688-44a3afc2c360",
//   "d904e066-1c35-4525-b971-7aaaa8cbde44",
// ];

// const EDUCATION_CHECK_IDS = [
//   "550f294a-257d-4b87-a0e7-8b34a3223d41",
//   "a687f91f-1c4c-4761-a4fe-37a564887783",
//   "66b2e258-615e-4998-b51b-b8b7ec5a2417",
// ];

// // const PANCARD_CHECK_IDS = [
// //   "8ff47bc3-a23c-455a-9970-4fff6a685edd",
// //   "12c919a4-0f58-404a-b147-5a8aea9ab00b",
// // ];

// // All "Drug Test - 4/5/8/9/10/12 Panel" are treated as ONE check
// const isDrugTest = (check: any) => /^drug test/i.test(check?.check_name ?? "");

// const isNonAssisted = (check: any) => check?.check_type === "NON-ASSISTED";

// const CreatePackage = () => {
//   const navigate = useNavigate();

//   const [countries, setCountries] = useState<any[]>([]);
//   const [checks, setChecks] = useState<any[]>([]); // headers from API
//   const [formData, setFormData] = useState({
//     packageName: "",
//     geoId: "",
//     packageCode: "",
//   });
//   const [errors, setErrors] = useState({
//     packageName: "",
//     geoId: "",
//     packageCode: "",
//   });

//   const [selectedChecks, setSelectedChecks] = useState<string[]>([]);
//   const defaultsApplied = useRef(false);

//   const getCountries = useApi<any>(webservice_url.countries, {
//     method: "GET",
//     autoCall: false,
//     handleGlobalError: false,
//   });

//   const createPackage = useApi<any>("/api/v1/admin/packages/checks", {
//     method: "POST",
//     autoCall: false,
//     handleGlobalError: false,
//   });

//   const getCheckHeader = useApi<any>("/api/v1/admin/check-headers", {
//     method: "GET",
//     autoCall: false,
//     handleGlobalError: false,
//   });

//   const checkHeaderLoading = getCheckHeader.loading;
//   const createLoading = createPackage.loading;

//   useEffect(() => {
//     const fetchDropdowns = async () => {
//       try {
//         const countriesRes = await getCountries.callApi();
//         if (countriesRes?.success) {
//           setCountries(countriesRes.data || []);
//         }
//       } catch (error) {
//         console.error("Dropdown API Error:", error);
//       }
//     };
//     const fetchCheckHeader = async () => {
//       try {
//         const res = await getCheckHeader.callApi();
//         if (res?.success) {
//           setChecks(res.data || []);
//         }
//       } catch (error) {
//         Swal.fire({
//           icon: "error",
//           title: "Error",
//           text: "Unable to load checks.",
//         });
//       }
//     };
//     fetchDropdowns();
//     fetchCheckHeader();
//   }, []);

//   /* ---------- Derived data ---------- */

//   // Flatten checks from all headers
//   const allChecks = useMemo(
//     () => checks.flatMap((header: any) => header.checks ?? []),
//     [checks],
//   );

//   const nonAssistedChecks = useMemo(
//     () => allChecks.filter(isNonAssisted),
//     [allChecks],
//   );

//   // ASSISTED + BOTH (e.g. PAN Card) go in the Assisted group
//   const assistedChecks = useMemo(
//     () => allChecks.filter((c: any) => !isNonAssisted(c)),
//     [allChecks],
//   );

//   const drugTestIds = useMemo(
//     () => allChecks.filter(isDrugTest).map((c: any) => c.check_id),
//     [allChecks],
//   );

//   // Scope of Package text, derived from the selection
//   const sop = useMemo(
//     () =>
//       allChecks
//         .filter((c: any) => selectedChecks.includes(c.check_id))
//         .map((c: any) => c.check_name)
//         .join(" + "),
//     [allChecks, selectedChecks],
//   );

//   /* ---------- Default selection ---------- */

//   // Pre-select all Non-Assisted checks once (except Drug Tests: pick one manually)
//   useEffect(() => {
//     if (defaultsApplied.current || !allChecks.length) return;
//     defaultsApplied.current = true;

//     const defaults = allChecks
//       .filter((c: any) => isNonAssisted(c) && !isDrugTest(c))
//       .map((c: any) => c.check_id);

//     setSelectedChecks((prev) => Array.from(new Set([...prev, ...defaults])));
//   }, [allChecks]);

//   /* ---------- Toggle ---------- */

//   const handleCheckToggle = (checkId: string) => {
//     setSelectedChecks((prev) => {
//       // Unselect
//       if (prev.includes(checkId)) {
//         return prev.filter((id) => id !== checkId);
//       }

//       // Drug test: only one panel at a time
//       if (drugTestIds.includes(checkId)) {
//         return [...prev.filter((id) => !drugTestIds.includes(id)), checkId];
//       }

//       // Employment: only one variant at a time
//       if (EMPLOYMENT_CHECK_IDS.includes(checkId)) {
//         return [
//           ...prev.filter((id) => !EMPLOYMENT_CHECK_IDS.includes(id)),
//           checkId,
//         ];
//       }
//       // EDUCATION: only one variant at a time
//       if (EDUCATION_CHECK_IDS.includes(checkId)) {
//         return [
//           ...prev.filter((id) => !EDUCATION_CHECK_IDS.includes(id)),
//           checkId,
//         ];
//       }
//       // PANCARD: only one variant at a time
//       // if (PANCARD_CHECK_IDS.includes(checkId)) {
//       //   return [
//       //     ...prev.filter((id) => !PANCARD_CHECK_IDS.includes(id)),
//       //     checkId,
//       //   ];
//       // }

//       // Normal checks: multiple allowed
      
//       return [...prev, checkId];
//     });
//   };

//   const clearError = (field: string) => {
//     setErrors((prev) => ({ ...prev, [field]: "" }));
//   };

//   const handleCreatePackage = async () => {
//     const result = packageSchema.safeParse(formData);

//     if (!result.success) {
//       const fieldErrors = result.error.flatten().fieldErrors;
//       setErrors({
//         packageName: fieldErrors.packageName?.[0] || "",
//         geoId: fieldErrors.geoId?.[0] || "",
//         packageCode: fieldErrors.packageCode?.[0] || "",
//       });
//       return;
//     }

//     if (selectedChecks.length === 0) {
//       Swal.fire({
//         icon: "warning",
//         title: "Validation",
//         text: "Please select at least one check.",
//       });
//       return;
//     }

//     const payload = {
//       packageName: formData.packageName,
//       geoId: formData.geoId,
//       packageCode: formData.packageCode,
//       sop,
//       checks: selectedChecks.map((id) => ({ checkId: id })),
//     };

//     try {
//       const res = await createPackage.callApi(payload);

//       if (res?.success) {
//         Swal.fire({
//           icon: "success",
//           title: "Success",
//           text: "Package created successfully.",
//         });
//         navigate(all_routes.allPackage);
//       } else {
//         Swal.fire({
//           icon: "error",
//           title: "Error",
//           text: res?.error || res?.message || "Something went wrong.",
//         });
//       }
//     } catch (error) {
//       Swal.fire({
//         icon: "error",
//         title: "Error",
//         text: "Unable to create package.",
//       });
//     }
//   };

//   /* ---------- Group renderer ---------- */

//   const renderGroup = (title: string, subtitle: string, list: any[]) => {
//     if (!list.length) return null;

//     const selectedCount = list.filter((c: any) =>
//       selectedChecks.includes(c.check_id),
//     ).length;

//     return (
//       <div className="mb-4">
//         <div className="d-flex justify-content-between align-items-center mb-3">
//           <div>
//             <h5 className="fw-bold mb-1">{title}</h5>
//             <small className="text-muted">{subtitle}</small>
//           </div>

//           <span
//             className="badge"
//             style={{
//               background: selectedCount ? "#0F6E56" : "#E1F5EE",
//               color: selectedCount ? "#fff" : "#085041",
//             }}
//           >
//             {selectedCount} Selected
//           </span>
//         </div>

//         <div className="row">
//           {list.map((check: any) => {
//             const selected = selectedChecks.includes(check.check_id);

//             return (
//               <div
//                 key={check.check_id}
//                 className="col-xl-3 col-lg-4 col-md-6 mb-3"
//               >
//                 <div
//                   onClick={() => handleCheckToggle(check.check_id)}
//                   className="rounded-3 px-3 py-3"
//                   style={{
//                     cursor: "pointer",
//                     transition: "all .2s ease",
//                     backgroundColor: selected ? "#E1F5EE" : "#fff",
//                     border: selected
//                       ? "1px solid #0F6E56"
//                       : "1px solid #E5E3DC",
//                   }}
//                 >
//                   <div className="d-flex align-items-center justify-content-between">
//                     <h6
//                       className="mb-0 fw-semibold"
//                       style={{
//                         color: selected ? "#085041" : "#2C2C2A",
//                         fontSize: "15px",
//                       }}
//                     >
//                       {check.check_name}
//                     </h6>

//                     <input
//                       type="checkbox"
//                       className="form-check-input"
//                       checked={selected}
//                       onClick={(e) => e.stopPropagation()}
//                       onChange={() => handleCheckToggle(check.check_id)}
//                       style={{
//                         width: 18,
//                         height: 18,
//                         cursor: "pointer",
//                         accentColor: "#0F6E56",
//                       }}
//                     />
//                   </div>
//                 </div>
//               </div>
//             );
//           })}
//         </div>
//       </div>
//     );
//   };

//   return (
//     <div className="page-wrapper">
//       <div className="content">
//         {/* Breadcrumb */}
//         <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
//           <div className="my-auto mb-2">
//             <h2 className="mb-1">Create Package</h2>
//             <nav>
//               <ol className="breadcrumb mb-0">
//                 <li className="breadcrumb-item">
//                   <Link to={all_routes.superAdminDashboard}>
//                     <i className="ti ti-smart-home" />
//                   </Link>
//                 </li>
//                 <li className="breadcrumb-item">Account Activation</li>
//                 <li className="breadcrumb-item">Account Configuration</li>
//                 <li className="breadcrumb-item">Package</li>
//                 <li className="breadcrumb-item active">Create Package</li>
//               </ol>
//             </nav>
//           </div>
//           <div className="head-icons ms-2">
//             <CollapseHeader />
//           </div>
//         </div>

//         <div className="card mt-3 shadow-sm border-0">
//           <div className="card-body">
//             <div>
//               <h4 className="fw-bold mb-4">Create Package</h4>
//               <hr className="bg-dark mt-0" />

//               {/* Top Fields */}
//               <div className="row g-3 mb-2">
//                 <div className="col-md-4">
//                   <label className="form-label ">
//                     Package Name <span className="text-danger">*</span>
//                   </label>
//                   <input
//                     type="text"
//                     className={`form-control ${errors.packageName ? "is-invalid" : ""}`}
//                     value={formData.packageName}
//                     onChange={(e) => {
//                       setFormData({ ...formData, packageName: e.target.value });
//                       clearError("packageName");
//                     }}
//                   />
//                   {errors.packageName && (
//                     <div className="invalid-feedback">{errors.packageName}</div>
//                   )}
//                 </div>

//                 <div className="col-md-4">
//                   <label className="form-label ">
//                     GEO <span className="text-danger">*</span>
//                   </label>
//                   <select
//                     className={`form-select ${errors.geoId ? "is-invalid" : ""}`}
//                     value={formData.geoId}
//                     onChange={(e) => {
//                       setFormData({ ...formData, geoId: e.target.value });
//                       clearError("geoId");
//                     }}
//                   >
//                     <option value="">Select GEO</option>
//                     {countries.map((item: any) => (
//                       <option key={item.country_id} value={item.country_id}>
//                         {item.country_name}
//                       </option>
//                     ))}
//                   </select>
//                   {errors.geoId && (
//                     <div className="invalid-feedback">{errors.geoId}</div>
//                   )}
//                 </div>

//                 <div className="col-md-4">
//                   <label className="form-label ">
//                     Package Code <span className="text-danger">*</span>
//                   </label>
//                   <input
//                     type="text"
//                     className={`form-control ${errors.packageCode ? "is-invalid" : ""}`}
//                     value={formData.packageCode}
//                     onChange={(e) => {
//                       setFormData({ ...formData, packageCode: e.target.value });
//                       clearError("packageCode");
//                     }}
//                   />
//                   {errors.packageCode && (
//                     <div className="invalid-feedback">{errors.packageCode}</div>
//                   )}
//                 </div>
//               </div>

//               <div className="mb-4">
//                 <label className="form-label">Scope of Package</label>
//                 <input
//                   type="text"
//                   className="form-control"
//                   value={sop}
//                   disabled
//                 />
//               </div>
//             </div>
//             <hr className="bg-dark mt-0" />
//             {checkHeaderLoading ? (
//               <SnipperLoader />
//             ) : (
//               <div>
//                 {renderGroup(
//                   "Non-Assisted Checks",
//                   "Included by default. For Drug Test, choose only one panel.",
//                   nonAssistedChecks,
//                 )}
//                 {renderGroup(
//                   "Assisted Checks",
//                   "Select the assisted checks to include in this package.",
//                   assistedChecks,
//                 )}
//               </div>
//             )}

//             <div className="d-flex justify-content-end gap-2 align-items-center mt-3">
//               <button
//                 type="button"
//                 className="btn btn-outline-primary d-flex align-items-center gap-2"
//                 onClick={() => navigate(-1)}
//               >
//                 <span>Back</span>
//               </button>
//               <button
//                 type="button"
//                 className="btn btn-primary d-flex align-items-center gap-2"
//                 onClick={handleCreatePackage}
//                 disabled={createLoading}
//               >
//                 <span>{createLoading ? "Creating..." : "Create"}</span>
//               </button>
//             </div>
//           </div>
//         </div>
//       </div>

//       <Footer />
//     </div>
//   );
// };

// export default CreatePackage; 